import React, { useMemo } from 'react';
import { Sparkles, Clock, Repeat, CalendarDays, Trophy } from 'lucide-react';
import { useI18n, formatDateWithMonthName } from '../i18n';
import CategoryIcon from './CategoryIcon';
import { getMemberHex, getCategoryColor, getCategoryName, formatarMinutos, sortMembrosBySeniority } from '../utils';
import { Badge, Card, EmptyState, PageHeader } from './ui';

export const MIN_ENTRIES_FOR_INSIGHTS = 50;
const TOP_TASKS = 10;
const DAY_MS = 24 * 60 * 60 * 1000;

// Per-task patterns for the most logged tasks: average duration, cadence,
// this month's count and leader, who does it overall.
export function buildInsights({ registros, tarefas, membros }, now = new Date()) {
  const tarefaById = new Map(tarefas.map(tf => [tf.id, tf]));
  const membroById = new Map(membros.map(m => [m.id, m]));
  const inicioMes = new Date(now.getFullYear(), now.getMonth(), 1);
  const porTarefa = new Map();

  registros.forEach(reg => {
    const tarefa = tarefaById.get(reg.tarefa_id);
    const membro = membroById.get(reg.membro_id);
    if (!tarefa || !membro) return;
    if (!porTarefa.has(tarefa.id)) {
      porTarefa.set(tarefa.id, { tarefa, count: 0, minutos: 0, primeiro: null, ultimo: null, porMembro: new Map(), mesPorMembro: new Map(), mesCount: 0 });
    }
    const stat = porTarefa.get(tarefa.id);
    const quando = new Date(reg.criado_em);
    stat.count += 1;
    stat.minutos += reg.tempo_minutos || 0;
    if (!stat.primeiro || quando < stat.primeiro) stat.primeiro = quando;
    if (!stat.ultimo || quando > stat.ultimo.quando) stat.ultimo = { quando, membro };
    stat.porMembro.set(membro.id, (stat.porMembro.get(membro.id) || 0) + 1);
    if (quando >= inicioMes) {
      stat.mesCount += 1;
      stat.mesPorMembro.set(membro.id, (stat.mesPorMembro.get(membro.id) || 0) + 1);
    }
  });

  const ordered = sortMembrosBySeniority(membros);
  return [...porTarefa.values()]
    .sort((a, b) => b.count - a.count)
    .slice(0, TOP_TASKS)
    .map(stat => {
      const spanDays = stat.primeiro && stat.ultimo ? (stat.ultimo.quando - stat.primeiro) / DAY_MS : 0;
      const cadenceDays = stat.count >= 3 && spanDays > 0 ? Math.max(1, Math.round(spanDays / (stat.count - 1))) : null;
      const liderMes = [...stat.mesPorMembro.entries()].sort((a, b) => b[1] - a[1])[0];
      return {
        tarefa: stat.tarefa,
        count: stat.count,
        mediaMinutos: stat.count ? stat.minutos / stat.count : 0,
        cadenceDays,
        mesCount: stat.mesCount,
        liderMes: liderMes ? { membro: membroById.get(liderMes[0]), count: liderMes[1] } : null,
        ultimo: stat.ultimo,
        share: ordered
          .filter(m => stat.porMembro.has(m.id))
          .map(m => ({ membro: m, count: stat.porMembro.get(m.id), pct: Math.round((stat.porMembro.get(m.id) / stat.count) * 100) })),
      };
    });
}

export default function Insights({ data }) {
  const { t, lang } = useI18n();
  const insights = useMemo(() => buildInsights(data), [data]);
  const categoriaById = useMemo(() => new Map(data.categorias.map(c => [c.id, c])), [data.categorias]);
  const enough = data.registros.length >= MIN_ENTRIES_FOR_INSIGHTS;

  return (
    <div>
      <PageHeader
        title={t('insights.title')}
        subtitle={enough ? t('insights.subtitle', { count: insights.length }) : null}
      />
      {!enough ? (
        <EmptyState
          icon={Sparkles}
          title={t('insights.notEnoughTitle')}
          body={t('insights.notEnoughBody', { count: data.registros.length, min: MIN_ENTRIES_FOR_INSIGHTS })}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {insights.map(item => {
            const categoria = categoriaById.get(item.tarefa.categoria_id);
            const facts = [
              { icon: Clock, text: t('insights.avgDuration', { minutes: formatarMinutos(item.mediaMinutos) }) },
              item.cadenceDays ? { icon: Repeat, text: t('insights.cadence', { days: item.cadenceDays }) } : null,
              item.liderMes
                ? { icon: Trophy, text: t('insights.topThisMonth', { name: item.liderMes.membro.nome, count: item.liderMes.count }) }
                : { icon: Trophy, text: t('insights.noneThisMonth') },
              item.ultimo ? { icon: CalendarDays, text: t('insights.lastDone', { date: formatDateWithMonthName(item.ultimo.quando, lang), name: item.ultimo.membro.nome }) } : null,
            ].filter(Boolean);
            return (
              <Card key={item.tarefa.id} data-insight className="p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  {categoria && (
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-md flex-shrink-0 ${getCategoryColor(categoria.id)}`} title={getCategoryName(categoria, t)}>
                      <CategoryIcon iconName={categoria.icone} size={15} />
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <h2 className="text-base font-semibold text-gray-900 truncate">{item.tarefa.nome}</h2>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      <Badge tone="gray">{t('insights.timesTotal', { count: item.count })}</Badge>
                      <Badge tone={item.mesCount ? 'purple' : 'gray'}>{t('insights.timesThisMonth', { count: item.mesCount })}</Badge>
                    </div>
                  </div>
                </div>

                <ul className="mt-4 space-y-2">
                  {facts.map((fact, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <fact.icon size={15} className="text-gray-400 mt-0.5 flex-shrink-0" />
                      <span>{fact.text}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">{t('insights.shareTitle')}</div>
                  <div className="flex h-2 rounded-full overflow-hidden bg-gray-100">
                    {item.share.map(s => (
                      <div key={s.membro.id} style={{ width: `${s.pct}%`, backgroundColor: getMemberHex(s.membro.id, data.membros) }} title={`${s.membro.nome} · ${s.pct}%`} />
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                    {item.share.map(s => (
                      <div key={s.membro.id} className="flex items-center gap-1.5 text-xs text-gray-600 tabular-nums">
                        <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: getMemberHex(s.membro.id, data.membros) }} />
                        {s.membro.nome} · {s.pct}%
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
